// FilePreviewCell.tsx - 带下载按钮的版本
import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Button } from '@material-ui/core'
import { CloudDownload } from '@material-ui/icons'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  fileLink: {
    padding: '6px 12px',
    background: '#f1f8e9',
    color: '#1A3C34',
    border: '1px solid #8bc34a',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    textDecoration: 'none',
    flex: 1,
    minWidth: 0,
    '&:hover': {
      background: '#8bc34a',
      color: '#ffffff',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(139, 195, 74, 0.3)',
      textDecoration: 'none',
    },
  },
  downloadButton: {
    minWidth: 'auto',
    padding: '6px',
    background: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #90caf9',
    borderRadius: '6px',
    '&:hover': {
      background: '#bbdefb',
      transform: 'translateY(-1px)',
    },
  },
  fileName: {
    maxWidth: '150px', 
    overflow: 'hidden', 
    textOverflow: 'ellipsis', 
    whiteSpace: 'nowrap',
    flex: 1,
  },
  loading: {
    opacity: 0.7,
  },
})

interface FilePreviewCellProps {
  url: string
  fileName?: string
}

const fileTypeCache = new Map<string, 'image' | 'pdf' | 'other'>()

const FilePreviewCell: React.FC<FilePreviewCellProps> = ({ url, fileName }) => {
  const classes = useStyles()
  const [fileType, setFileType] = useState<'image' | 'pdf' | 'other'>('other')
  const [loading, setLoading] = useState(false)

  const getFileTypeFromExtension = (): 'image' | 'pdf' | 'other' => {
    if (!fileName && !url) return 'other'
    
    const fileStr = (fileName || url).toLowerCase()
    
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
    if (imageExtensions.some(ext => fileStr.includes(ext))) {
      return 'image'
    }
    
    if (fileStr.includes('.pdf')) {
      return 'pdf'
    }
    
    return 'other'
  }

  const detectFileTypeFromHeadRequest = async (fileUrl: string) => {
    if (fileTypeCache.has(fileUrl)) {
      setFileType(fileTypeCache.get(fileUrl)!)
      return
    }

    const extensionType = getFileTypeFromExtension()
    if (extensionType !== 'other') {
      setFileType(extensionType)
      fileTypeCache.set(fileUrl, extensionType)
      return
    }

    setLoading(true)
    
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 3000)
      
      const response = await fetch(fileUrl, { 
        method: 'HEAD',
        mode: 'cors',
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      
      const contentType = response.headers.get('Content-Type')
      let detectedType: 'image' | 'pdf' | 'other' = 'other'
      
      if (contentType) {
        if (contentType.includes('pdf') || contentType.includes('application/pdf')) {
          detectedType = 'pdf'
        } else if (contentType.startsWith('image/')) {
          detectedType = 'image'
        }
      }
      
      setFileType(detectedType)
      fileTypeCache.set(fileUrl, detectedType)
      
    } catch (error) {
      console.warn('HEAD请求失败:', error)
      setFileType('other')
      fileTypeCache.set(fileUrl, 'other')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (url) {
      detectFileTypeFromHeadRequest(url)
    }
  }, [url])

  // 强制预览的方法
  const forcePreview = (e: React.MouseEvent) => {
    e.preventDefault()
    
    if (fileType === 'image') {
      // 对于图片，使用新的窗口打开
      const imageWindow = window.open('', '_blank')
      if (imageWindow) {
        imageWindow.document.write(`
          <html>
            <head>
              <title>图片预览 - ${fileName || '未命名文件'}</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: calc(100vh - 40px);
                  background: #f5f5f5;
                }
                img { 
                  max-width: 90vw; 
                  max-height: 90vh; 
                  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                  border-radius: 8px;
                }
              </style>
            </head>
            <body>
              <img src="${url}" alt="${fileName || '预览'}" onerror="window.close()" />
            </body>
          </html>
        `)
        imageWindow.document.close()
      }
    } else if (fileType === 'pdf') {
      // 对于PDF，使用Google Docs Viewer或其他在线PDF查看器
      const pdfViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`
      window.open(pdfViewerUrl, '_blank')
    } else {
      // 其他文件类型直接下载
      handleDownload()
    }
  }

  // 下载文件的方法
  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName || getDisplayFileName()
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const getFileIcon = () => {
    if (loading) {
      return '⏳'
    }
    
    switch (fileType) {
      case 'image':
        return '🖼️'
      case 'pdf':
        return '📄'
      default:
        return '📎'
    }
  }

  const getDisplayFileName = () => {
    if (fileName) {
      return fileName
    }
    
    try {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      const filenameFromUrl = pathname.split('/').pop()
      return filenameFromUrl || 'view file'
    } catch {
      return 'view file'
    }
  }

  const getLinkTitle = () => {
    switch (fileType) {
      case 'image':
        return 'preview image'
      case 'pdf':
        return 'preview pdf'
      default:
        return 'donwload file'
    }
  }

  return (
    <div className={classes.container}>
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className={`${classes.fileLink} ${loading ? classes.loading : ''}`}
        title={getLinkTitle()}
        onClick={forcePreview}
      >
        <span>{getFileIcon()}</span>
        <span className={classes.fileName}>
          {getDisplayFileName()}
        </span>
      </a>
      
      <Button
        className={classes.downloadButton}
        size="small"
        onClick={handleDownload}
        title="Download File"
      >
        <CloudDownload fontSize="small" />
      </Button>
    </div>
  )
}

export default FilePreviewCell